import React from 'react';
import styled, { css } from 'styled-components';
import { bool, element, string, oneOfType } from 'prop-types';
import { ButtonColorSchema } from '@airasia-common/libraries/constants';
import { getColorSchema } from '@airasia-common/libraries/theming/theme';

const activeBtnStyle = css`
  background-color: ${props =>
    props?.backgroundColor
      ? props?.backgroundColor
      : getColorSchema(props)?.background?.normal};
  border: 1px solid
    ${props =>
      props?.border ? props?.border : getColorSchema(props)?.border?.normal};
  cursor: pointer;
`;

const disabledBtnStyle = css`
  background-color: ${props => getColorSchema(props)?.background?.disabled};
  border: 1px solid ${props => getColorSchema(props)?.border?.disabled};
  box-shadow: none;
  pointer-events: none;
  cursor: not-allowed;
`;

const Icon = styled.span`
  margin-right: 8px;
`;

const IconWrapper = styled.div`
  padding-top: 4px;
`;

const Text = styled.span`
  padding: ${({ padding }) => (padding ? padding : '0 8px')};
  color: ${props =>
    props.disabled
      ? getColorSchema(props)?.text?.disabled
      : props.border
      ? props.border
      : getColorSchema(props)?.text?.normal};
  font-size: ${({ big, schema }) =>
    big ? schema?.big?.fontSize : schema?.small?.fontSize}px;
  line-height: 20px;
  font-weight: 500;
  display: flex;
  justify-content: center;
  align-content: center;
  margin-top: ${({ errorButton }) => (errorButton ? '12px' : '')};

  &:hover {
    color: ${props =>
      props.border ? props.border : getColorSchema(props)?.text?.hover};
  }

  &:active {
    color: ${props => getColorSchema(props)?.text?.clicked};
  }
`;
Text.displayName = 'Text';

const StyledButton = styled.button`
  ${({ disabled }) => (disabled ? disabledBtnStyle : activeBtnStyle)};
  height: ${({ big, schema }) =>
    big ? schema.big.height : schema.small.height}px;
  box-shadow: ${({ clicked }) =>
    clicked ? 'none' : '0px 1px 1px rgba(0, 0, 0, 0.2)'};
  width: ${({ block }) => (block ? '100%' : 'auto')};
  align-items: center;
  justify-content: center;
  border-radius: ${props =>
    props?.borderRadius ? props?.borderRadius : '4px'};
  position: relative;
  outline: none;
  display: flex;

  ${Icon} {
    color: ${props =>
      props.disabled
        ? getColorSchema(props)?.text?.disabled
        : getColorSchema(props)?.text?.icon};
  }

  &:hover {
    background-color: ${props =>
      props?.hovercolor
        ? props?.hovercolor
        : getColorSchema(props)?.background?.hover};
    border-color: ${props =>
      props?.border ? props?.border : getColorSchema(props)?.border?.hover};
    box-shadow: ${({ move, tertiary }) =>
      move ? 'none' : `0 ${tertiary ? 1 : 2}px 4px rgba(0, 0, 0, 0.2)`};
  }

  ${({ move }) => !move} {
    &:focus .focus-outline {
      outline: none;
      display: block;
    }
  }

  &:active {
    background-color: ${props =>
      props?.hovercolor
        ? props?.hovercolor
        : getColorSchema(props)?.background?.clicked};
  }

  &:active ${Text} {
    color: ${props =>
      props?.border ? props?.border : getColorSchema(props)?.text?.clicked};
  }

  &:after {
    content: '';
    position: absolute;
    top: -8px;
    bottom: -8px;
    left: -8px;
    right: -8px;
  }
`;

const Outline = styled.span`
  display: none;
  outline: none;
  position: absolute;
  top: -7px;
  bottom: -7px;
  right: -7px;
  left: -7px;
  width: calc(100% + 12px);
  background-color: transparent;
  border: 1px solid ${props => getColorSchema(props)?.focus};
  border-radius: 4px;
`;

const Button = ({
  children,
  primary,
  secondary,
  tertiary,
  big,
  disabled,
  padding,
  block,
  icon,
  move,
  errorButton,
  ...props
}) => {
  return (
    <StyledButton
      schema={ButtonColorSchema}
      primary={primary}
      secondary={secondary}
      tertiary={tertiary}
      big={big}
      disabled={disabled}
      block={block}
      icon={icon}
      move={move}
      {...props}
    >
      <Outline schema={ButtonColorSchema} className="focus-outline" />

      {icon && (
        <IconWrapper>
          <Icon>{icon}</Icon>
        </IconWrapper>
      )}
      <Text
        padding={padding}
        big={big}
        primary={primary}
        secondary={secondary}
        tertiary={tertiary}
        schema={ButtonColorSchema}
        disabled={disabled}
        icon={icon}
        errorButton={errorButton}
        {...props}
      >
        {children}
      </Text>
    </StyledButton>
  );
};

Button.propTypes = {
  children: oneOfType([string, element]),
  primary: bool,
  secondary: bool,
  tertiary: bool,
  big: bool,
  disabled: bool,
  padding: string,
  block: bool,
  icon: element,
  move: bool,
  errorButton: bool,
};

export default Button;
